import React from "react";
import { APP_LINKS, SITE } from "@/lib/site";
import { ArrowRight, Apple, Smartphone, MapPin, Droplet } from "lucide-react";

export default function Hero() {
  return (
    <section id="top" className="water-grad relative overflow-hidden pt-28 md:pt-36 pb-20">
      {/* ambient drops */}
      <div className="absolute inset-0 pointer-events-none dotgrid opacity-40" />
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#0ea5e9]/10 blur-3xl" />
      <div className="absolute bottom-10 -left-32 w-[420px] h-[420px] rounded-full bg-[#0369a1]/10 blur-3xl" />

      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-sky-100 text-[12px] text-[#0b1d2a]">
              <MapPin size={13} className="text-[#0369a1]" />
              {SITE.shortAddress}
            </div>

            <h1 className="serif h-display mt-6 text-5xl sm:text-6xl lg:text-7xl text-[#0b1d2a]">
              Pure Water. <br />
              <span className="italic text-[#0369a1]">Delivered Smarter.</span>
            </h1>

            <p className="mt-6 max-w-xl text-slate-600 text-base sm:text-lg leading-relaxed">
              Reliable, technology-driven water supply solutions across {SITE.city} — now at your fingertips. Click. Order. Delivered.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={APP_LINKS.ios}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download the Wateriia app on the App Store"
                data-testid="hero-app-store"
                className="group inline-flex items-center gap-3 rounded-2xl bg-[#0b1d2a] text-white px-5 py-3.5 hover:bg-[#0369a1] transition-all"
              >
                <Apple size={20} />
                <div className="leading-tight text-left">
                  <div className="text-[10px] uppercase tracking-widest opacity-70">Download on the</div>
                  <div className="text-sm font-semibold">App Store</div>
                </div>
              </a>
              <a
                href={APP_LINKS.android}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get the Wateriia app on Google Play"
                data-testid="hero-play-store"
                className="group inline-flex items-center gap-3 rounded-2xl bg-white border border-sky-100 px-5 py-3.5 hover:border-[#0ea5e9] transition-all"
              >
                <Smartphone size={20} className="text-[#0369a1]" />
                <div className="leading-tight text-left">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Get it on</div>
                  <div className="text-sm font-semibold">Google Play</div>
                </div>
              </a>
              <a
                href="#contact"
                data-testid="hero-contact-btn"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold text-[#0b1d2a] hover:text-[#0369a1] transition"
              >
                Contact Us <ArrowRight size={16} />
              </a>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
              {[
                { k: "10K+", v: "Daily Deliveries" },
                { k: "24/7", v: "App Ordering" },
                { k: "100%", v: "Purity Checked" },
              ].map((s, i) => (
                <div key={i} data-testid={`hero-stat-${i}`} className="border-t border-sky-200/70 pt-3">
                  <div className="serif text-3xl text-[#0b1d2a]">{s.k}</div>
                  <div className="text-xs text-slate-500 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Phone Mockup */}
          <div className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-[320px] float-y">
              <div className="phone-frame">
                <div className="phone-screen aspect-[9/19]">
                  {/* notch */}
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-24 rounded-full bg-slate-900/80" />
                  {/* status bar */}
                  <div className="pt-10 px-5 flex items-center justify-between text-[10px] text-slate-500">
                    <span>9:41</span>
                    <span>Wateriia</span>
                  </div>
                  {/* card 1 */}
                  <div className="mt-4 mx-4 rounded-2xl bg-white shadow-sm p-4 border border-sky-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-emerald-600">Order Confirmed</div>
                        <div className="text-sm font-semibold mt-1">20L × 2 Cans</div>
                      </div>
                      <div className="h-9 w-9 rounded-full bg-sky-50 flex items-center justify-center">
                        <Droplet size={16} className="text-[#0ea5e9]" />
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                      <span>ETA</span>
                      <span className="text-[#0b1d2a] font-semibold">18 mins</span>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-sky-100 overflow-hidden">
                      <div className="h-full w-2/3 bg-[#0ea5e9]" />
                    </div>
                  </div>

                  {/* live tile */}
                  <div className="mt-3 mx-4 rounded-2xl bg-[#0b1d2a] text-white p-4 relative overflow-hidden">
                    <div className="flex items-center gap-2">
                      <span className="relative inline-flex">
                        <span className="h-2 w-2 rounded-full bg-emerald-400" />
                        <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping" />
                      </span>
                      <div className="text-[10px] uppercase tracking-widest opacity-70">Live</div>
                    </div>
                    <div className="mt-1 text-sm font-semibold">Rider en route</div>
                    <div className="mt-2 text-[11px] opacity-70">800m away · {SITE.city}</div>
                    <div className="absolute -right-6 -bottom-6 h-24 w-24 rounded-full bg-[#0ea5e9]/30 blur-2xl" />
                  </div>

                  <div className="mt-3 mx-4 grid grid-cols-2 gap-2">
                    <div className="rounded-xl bg-sky-50 p-3">
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest">Reorder</div>
                      <div className="text-sm font-semibold mt-1">20L × 2 · 1‑Tap</div>
                    </div>
                    <div className="rounded-xl bg-sky-50 p-3">
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest">Pay</div>
                      <div className="text-sm font-semibold mt-1">UPI · Cards</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* floating badges */}
              <div className="absolute -left-8 top-12 rotate-[-6deg] bg-white rounded-2xl shadow-lg px-3 py-2 border border-sky-100">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Avg.</div>
                <div className="serif text-lg">18 min</div>
              </div>
              <div className="absolute -right-6 bottom-16 rotate-[6deg] bg-[#0b1d2a] text-white rounded-2xl shadow-lg px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest opacity-70">Rating</div>
                <div className="serif text-lg">4.8 ★</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
